<template>
  <div
    :class="[
      'ine-card sonar-assessment-card',
      `sonar-assessment-card${assessmentGroupData.postfix}`,
    ]"
  >
    <div class="sonar-assessment-card__bg" />
    <div class="sonar-assessment-card__container">
      <div class="sonar-assessment-card__content-wrapper">
        <div class="sonar-assessment-card__info-wrapper">
          <div class="pill pill--secondary sonar-assessment-card__info">
            {{ assessmentInfo }}
          </div>
          <div class="sonar-assessment-card__description-wrapper">
            <div class="sonar-assessment-card__name">
              {{ assessment.name }}
            </div>
            <div class="sonar-assessment-card__data-wrapper">
              <div class="sonar-assessment-card__difficulty">
                {{ assessment.difficulty }}
              </div>
              <div
                v-if="assessment.category"
                class="sonar-assessment-card__category"
              >
                <span>|</span> {{ assessment.category }}
              </div>
              <div v-if="isBeta" class="sonar-assessment-card__beta">
                BETA
              </div>
            </div>
          </div>
        </div>
        <div class="sonar-assessment-card__level-wrapper">
          <div
            v-for="(level, index) in assessmentLevels"
            :key="index"
            class="sonar-assessment-card__level"
          >
            <SonarLevelBadge
              :level="level"
              :isResultsModal="isResultsModal"
              :blockedByCoolingOff="
                !isAdminOrContentCreator && assessmentResseaseInterval
              "
              :coolingOffTime="coolingOffTime"
              :takenDate="takenDate"
              :previousLevelName="getPreviousLevelName(index)"
              :timerText="timerText"
              @start-assessment="sartPreviousAssessment"
            />
            <div
              :class="[
                'sonar-assessment-card__level-bar',
                isPassed(level) &&
                  `sonar-assessment-card__level-bar--${level.difficulty}`,
              ]"
            />
          </div>
        </div>
      </div>
      <div class="sonar-assessment-card__cta-wrapper">
        <div
          v-if="!isAdminOrContentCreator && assessmentResseaseInterval"
          class="sonar-assessment-card__reassess-wrapper"
        >
          <span>
            Reassess in <strong>{{ timerText }}!</strong>
          </span>
        </div>
        <button
          v-else
          class="btn btn--secondary btn--small"
          :disabled="isCtaDisabled"
          @click="goToAssessment()"
        >
          Test me
        </button>
        <button
          v-if="!isResultsModal"
          class="btn btn--secondary btn--small btn--icon"
          :disabled="isRecommendedLoading"
          @click="showRecommendedHandler()"
        >
          Recommended
          <ChevronRightIcon
            v-if="!isRecommendedLoading"
            :class="[`sonar-assessment-card__cta-icon${recommendedCardStatus}`]"
          />
          <ActionLoader
            v-else
            class="sonar-assessment-card__cta-loader"
            :name="'ball-beat'"
            :color="'#9e9e9e'"
            :size="0.3"
          />
        </button>
      </div>
    </div>
    <SonarRecommendedContent
      v-if="showRecommended"
      :parentIds="assessment.skill_areas"
      :difficulty="assessment.difficulty"
      :isCtaDisabled="isCtaDisabled"
      @start-assessment="goToAssessment()"
      @is-loading="isLoadingHandler"
    />
  </div>
</template>

<script>
import { differenceInSeconds, formatDistanceToNowStrict } from 'date-fns'
import { mapGetters } from 'vuex'
import SonarLevelBadge from '@/sonar/components/SonarLevelBadge.vue'
import SonarRecommendedContent from '@/sonar/components/SonarRecommendedContent.vue'
import CONSTANTS from '@/constants'
import { ChevronRightIcon } from 'vue-feather-icons'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import { groupSetup, assessmentLevel } from '@/sonar/helpers/utils'
import { assessmentHandler } from '@/sonar/helpers/mixins'

export default {
  name: 'SonarAssesmentCard',
  components: {
    SonarLevelBadge,
    ChevronRightIcon,
    SonarRecommendedContent,
    ActionLoader,
  },
  mixins: [assessmentHandler],
  props: {
    assessment: {
      type: Object,
      required: true,
    },
    isResultsModal: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isRecommendedLoading: false,
      assessmentGroupData: {},
      showRecommended: false,
      recommendedCardStatus: '',
      timer: 0,
      assessmentResseaseInterval: null,
      timerText: null,
    }
  },
  computed: {
    ...mapGetters({
      isAdminOrContentCreator: 'auth/isAdminOrContentCreator',
    }),
    isComingSoon() {
      const coming_soon = CONSTANTS.SONAR.ASSESSMENT.CONTENT_STATUS.COMING_SOON
      const comingSoon = coming_soon.replace('_', ' ')
      return (
        this.assessment?.status === coming_soon ||
        this.assessment?.status === comingSoon
      )
    },
    isBeta() {
      return this.assessment?.is_beta
    },
    assessmentInfo() {
      if (this.isComingSoon) return 'Coming soon'
      return this.assessment?.total_questions
        ? `${this.assessment.total_questions} questions`
        : ''
    },
    assessmentLevels() {
      const { difficulty, status, user_status } = this.assessment
      const payload = {
        difficulty,
        assessment_status: status,
        user_status,
      }
      return assessmentLevel(payload)
    },
    isCtaDisabled() {
      return this.isComingSoon
    },
    coolingOffTime() {
      return this.assessment.user_status?.retake_time_in_minutes
    },
    takenDate() {
      return this.assessment.user_status?.last_attempt_date
    },
  },
  watch: {
    assessment() {
      this.setResseasseValue()
    },
  },
  mounted() {
    this.assessmentGroupData = groupSetup(
      this.assessment?.learning_area_group?.slug
    )
    this.setResseasseValue()
  },
  destroyed() {
    clearInterval(this.assessmentResseaseInterval)
  },
  methods: {
    getPreviousLevelName(index) {
      if (!index || index === 0) {
        return null
      }
      const previousAssessment = this.assessmentLevels[index - 1]
      return previousAssessment
        ? this.assessmentLevels[index - 1]?.difficulty
        : null
    },
    isPassed(level) {
      return CONSTANTS.SONAR.ASSESSMENT.LEVEL_STATUS.PASSED === level.status
    },
    sartPreviousAssessment(selectedDifficulty) {
      const { required_assessments, difficulty } = this.assessment
      if (selectedDifficulty === difficulty) return this.goToAssessment()
      const assessment = required_assessments?.find(
        a => a.difficulty === selectedDifficulty
      )
      if (assessment) this.goToAssessment(assessment.id)
    },
    showRecommendedHandler() {
      this.isRecommendedLoading = true
      this.showRecommended = !this.showRecommended
      this.recommendedCardStatus = this.showRecommended
        ? '--opened'
        : '--closed'
    },
    isLoadingHandler(value) {
      this.isRecommendedLoading = value
    },
    stopTimer() {
      clearInterval(this.assessmentResseaseInterval)
      this.assessmentResseaseInterval = null
      this.timerText = null
    },
    setResseasseValue() {
      const retakeDate = this.assessment?.user_status?.datetime_to_retake
      if (!retakeDate) return
      const remaining = differenceInSeconds(
        new Date(retakeDate).getTime(),
        new Date().getTime()
      )
      if (remaining <= 0) return
      this.timer = remaining
      this.assessmentResseaseInterval = setInterval(() => {
        this.timerText = formatDistanceToNowStrict(new Date(retakeDate))
        if (this.timer <= 0) {
          this.stopTimer()
          return
        }
        this.timer -= 1
      }, 1000)
    },
  },
}
</script>

<style lang="scss" scoped>
.sonar-assessment-card {
  position: relative;
  display: flex;
  min-height: 177px;
  border-radius: 10px;
  gap: 10px;
  overflow: hidden;
  flex-direction: column;
  @include tablet {
    min-height: 84px;
    gap: 0;
    align-items: center;
  }
  &:hover {
    background: var(--glass-extra-light);
  }
  &--red-team,
  &--network-security {
    .sonar-assessment-card__bg {
      background: var(--sonar-radial-gradient-red);
    }
  }
  &--blue-team {
    .sonar-assessment-card__bg {
      background: var(--sonar-radial-gradient-blue);
    }
  }
  &--yellow-team {
    .sonar-assessment-card__bg {
      background: var(--sonar-radial-gradient-yellow);
    }
  }
  &--cisco,
  &--enterprise-infrastructure {
    .sonar-assessment-card__bg {
      background: var(--sonar-radial-gradient-cisco);
    }
  }
  &__container {
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    min-height: 137px;
    gap: 10px;
    padding: 20px;
    @include tablet {
      gap: 0;
      min-height: 44px;
      flex-direction: row;
    }
  }
  &__bg {
    position: absolute;
    width: 30%;
    height: 150px;
    left: -30px;
    filter: blur(37px);
    @include tablet {
      left: -85px;
    }
  }
  &__content-wrapper {
    display: flex;
    flex-direction: column-reverse;
    gap: 10px;
    width: 100%;
    z-index: 100;
    @include tablet {
      flex-direction: row;
    }
  }
  &__info-wrapper {
    display: flex;
    gap: 10px;
    align-items: center;
    width: 100%;
    @include tablet {
      gap: 30px;
    }
  }

  &__info {
    background: var(--charcoal);
    color: var(--white);
    padding: 6px 11px;
    width: 90px;
    height: fit-content;
    text-transform: none;
    white-space: nowrap;
  }

  &__description-wrapper {
    display: flex;
    flex-direction: column;
    color: var(--white);
    font-family: var(--font-bold);
  }
  &__name {
    font-size: rem(18);
    font-weight: 500;
  }
  &__data-wrapper {
    display: flex;
    gap: 8px;
  }
  &__difficulty,
  &__category,
  &__beta {
    font-size: rem(14);
    line-height: rem(20);
    text-transform: capitalize;
  }
  &__beta {
    color: var(--purple-pale);
    font-weight: 400;
  }
  &__difficulty {
    font-weight: 500;
  }
  &__category {
    display: flex;
    gap: 7px;
    font-family: var(--font-book);
    color: var(--light-blue-gray);
  }
  &__level-wrapper {
    display: flex;
    align-items: center;
    margin-left: auto;
  }
  &__level {
    display: flex;
    align-items: center;
    &:last-child .sonar-assessment-card__level-bar {
      display: none;
    }
  }
  &__level-bar {
    width: 33px;
    height: 3px;
    background: var(--tundora-gray);
    @include tablet {
      width: 72px;
    }
    &--novice {
      background: var(--sonar-gradient-1);
    }
    &--professional {
      background: var(--sonar-gradient-2);
    }
    &--advanced {
      background: var(--sonar-gradient-3);
    }
  }

  &__cta-wrapper {
    display: flex;
    align-items: center;
    margin-left: auto;
    gap: 10px;
    color: var(--white);
    @include mobile {
      padding-left: 30px;
    }
  }
  &__cta-icon {
    &--opened {
      animation: rotate 0.2s ease-in forwards;
    }
    &--closed {
      animation: rotate 0.2s ease-in reverse forwards;
    }
    @keyframes rotate {
      from {
        transform: rotate(0);
      }
      to {
        transform: rotate(90deg);
      }
    }
  }
  &__cta-loader {
    display: flex;
    max-width: 35px;
    margin-left: -20px;
    ::v-deep .vue-loaders {
      display: flex;
    }
  }
  &__reassess-wrapper {
    width: 83px;
    padding: 0;
    text-align: center;
    span {
      font-size: 12px;
    }
  }
}
</style>
